/** Sprint 33, story-170. Enum → translated label maps for the report view, keyed off the
 * `dailySiteReport` locale bundle returned by `useTranslation('dailySiteReport')`. */
export type DailySiteReportTranslations = Record<string, string>;

export function taskStatusLabel(t: DailySiteReportTranslations, status: string): string {
  const map: Record<string, string> = {
    active: t.taskStatusActive,
    inactive: t.taskStatusInactive,
    complete: t.taskStatusComplete,
    rejected: t.taskStatusRejected,
    canceled: t.taskStatusCanceled,
    internalApproval: t.taskStatusInternalApproval,
    clientApproval: t.taskStatusClientApproval,
    delegationCanceled: t.taskStatusDelegationCanceled,
  };
  return map[status] ?? status;
}

export function incidentTypeLabel(t: DailySiteReportTranslations, type: string | null): string {
  if (!type) return '';
  const map: Record<string, string> = {
    WEATHER: t.incidentTypeWeather,
    ACCIDENT: t.incidentTypeAccident,
    MATERIALS_ISSUE: t.incidentTypeMaterialsIssue,
    EQUIPMENT_ISSUE: t.incidentTypeEquipmentIssue,
    OTHER: t.incidentTypeOther,
  };
  return map[type] ?? type;
}

export function incidentStatusLabel(t: DailySiteReportTranslations, status: string): string {
  return status === 'ACKNOWLEDGED' ? t.incidentStatusAcknowledged : t.statusPending;
}

export function txTypeLabel(t: DailySiteReportTranslations, type: string): string {
  const map: Record<string, string> = {
    RECEIVE: t.txTypeReceive,
    CONSUME: t.txTypeConsume,
    ADJUST: t.txTypeAdjust,
    RETURN: t.txTypeReturn,
    RECOVERY: t.txTypeRecovery,
    TRANSFER: t.txTypeTransfer,
    USE_AS_EQUIPMENT: t.txTypeUseAsEquipment,
  };
  return map[type] ?? type;
}

/** Sprint 38, story-205. Type text for an incident: "Other: <free text>" when the type is OTHER
 * and the reporter typed one, else the type label. */
export function incidentTypeText(
  t: DailySiteReportTranslations,
  item: { incidentType: string | null; otherIncidentType?: string },
): string {
  return item.incidentType === 'OTHER' && item.otherIncidentType
    ? t.incidentTypeOtherNamed.replace('{type}', item.otherIncidentType)
    : incidentTypeLabel(t, item.incidentType);
}

export function delayUnitLabel(t: DailySiteReportTranslations, unit: string, n: number): string {
  const one = n === 1;
  const map: Record<string, string> = {
    HOURS: one ? t.delayUnitHoursOne : t.delayUnitHoursOther,
    DAYS: one ? t.delayUnitDaysOne : t.delayUnitDaysOther,
    WEEKS: one ? t.delayUnitWeeksOne : t.delayUnitWeeksOther,
  };
  return map[unit] ?? unit;
}

export function daysOpenLabel(t: DailySiteReportTranslations, days: number): string {
  if (days <= 0) return t.incidentOpenedToday;
  return days === 1 ? t.incidentDaysOpenOne : t.incidentDaysOpenOther.replace('{n}', String(days));
}

/** The muted detail parts of an incident row (reporter, delay, age); absent fields are omitted. */
export function incidentDetailParts(
  t: DailySiteReportTranslations,
  item: { reportedByName?: string; delay?: number; delayUnit?: string; ageDays?: number },
): string[] {
  const parts: string[] = [];
  if (item.reportedByName) parts.push(t.incidentReportedBy.replace('{name}', item.reportedByName));
  if (item.delay != null && item.delayUnit) {
    parts.push(
      t.incidentDelay.replace('{n}', String(item.delay)).replace('{unit}', delayUnitLabel(t, item.delayUnit, item.delay)),
    );
  }
  if (item.ageDays != null) parts.push(daysOpenLabel(t, item.ageDays));
  return parts;
}
